import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { NOTIF } from "@/constants/testIds";
import { Bell, BellRing, Check, Sparkles, Dumbbell, Droplet, Scale, CalendarCheck, MessageSquare, Send, Target } from "lucide-react";
import { toast } from "sonner";
import { requestNotificationPermission, startPushScheduler, sendTestPush } from "@/lib/pushScheduler";

const ICON_BY_KIND = {
  workout: Dumbbell,
  water: Droplet,
  weigh_in: Scale,
  meal_prep: Sparkles,
  check_in: CalendarCheck,
  focus: Target,
  message: Send,
  custom: MessageSquare,
};

// Where each reminder should take the user when they tap it.
const ROUTE_BY_KIND = {
  workout: "/workouts",
  water: "/dashboard",
  weigh_in: "/measurements",
  meal_prep: "/meals",
  check_in: "/dashboard",
  focus: "/workouts",
  custom: "/notifications",
};

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [permState, setPermState] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );

  const load = async () => {
    const r = await api.get("/notifications");
    setItems(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      await api.post("/notifications/seed_defaults");
      toast.success("Reminders generated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to seed");
    } finally {
      setSeeding(false);
    }
  };

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      load();
    } catch {}
  };

  const openNotification = async (n) => {
    // Mark-as-read and deep-link to the relevant page in a single tap. The
    // read call is fire-and-forget so navigation isn't blocked on the network.
    if (!n.read) {
      api.post(`/notifications/${n.id}/read`).catch(() => {});
      // Optimistic UI so the tapped card fades immediately.
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    // Server-provided deep_link (e.g. `/messages/<peer_id>`) wins over the
    // generic kind mapping so a message notif opens the exact conversation.
    const to = n.deep_link || ROUTE_BY_KIND[n.kind] || "/notifications";
    if (to !== "/notifications") navigate(to);
  };

  const enableBrowserPush = async () => {
    const state = await requestNotificationPermission();
    setPermState(state);
    if (state === "granted") {
      await startPushScheduler();
      toast.success("Browser reminders enabled");
    } else if (state === "denied") {
      toast.error("Permission denied. Enable notifications in browser settings.");
    } else if (state === "unsupported") {
      toast.error("This browser doesn't support notifications.");
    }
  };

  const fireTestPush = async () => {
    try {
      const r = await sendTestPush();
      if (r.delivered > 0) toast.success(`Test push delivered to ${r.delivered} device${r.delivered > 1 ? "s" : ""}`);
      else toast.error("No devices reachable. Try enabling browser push first.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test push failed");
    }
  };

  const grouped = items.reduce((acc, n) => {
    const day = new Date(n.scheduled_for).toLocaleDateString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
    acc[day] = acc[day] || [];
    acc[day].push(n);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-blaze">/ Notifications</div>
          <h1 className="font-heading text-5xl uppercase mt-1">Stay on plan.</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="notif-enable-push"
            onClick={enableBrowserPush}
            className={`px-4 py-2.5 text-xs uppercase tracking-widest rounded-sm inline-flex items-center gap-2 transition-colors border ${
              permState === "granted"
                ? "border-blaze text-blaze bg-obsidian-800"
                : "border-obsidian-600 text-white hover:bg-obsidian-700"
            }`}
          >
            <BellRing className="h-4 w-4" />
            {permState === "granted" ? "Browser push on" : "Enable browser push"}
          </button>
          {permState === "granted" && (
            <button
              data-testid="notif-test-push"
              onClick={fireTestPush}
              className="ghost-btn px-4 py-2.5 text-xs inline-flex items-center gap-2"
            >
              <Send className="h-3 w-3" /> Test push
            </button>
          )}
          <button data-testid={NOTIF.seed} onClick={seedDefaults} disabled={seeding} className="blaze-btn px-5 py-2.5 text-xs disabled:opacity-50 inline-flex items-center gap-2">
            <Bell className="h-4 w-4" /> {seeding ? "Generating..." : "Generate reminders"}
          </button>
        </div>
      </div>

      {permState === "default" && (
        <div className="obs-card border-blaze/40 !p-4 flex items-center gap-3">
          <BellRing className="h-5 w-5 text-blaze shrink-0" />
          <div className="flex-1 text-sm text-zinc-300">
            Turn on browser push to get native OS reminders when it's time to train, weigh in, or hydrate — even when this tab is in the background.
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="obs-card text-center py-16">
          <Bell className="h-10 w-10 text-blaze mx-auto mb-3" />
          <h3 className="font-heading text-2xl uppercase">All clear</h3>
          <p className="text-zinc-400 text-sm mt-2">Tap "Generate reminders" to auto-create workout, weigh-in & water reminders for the next 7 days.</p>
        </div>
      ) : (
        <div data-testid={NOTIF.list} className="space-y-6">
          {Object.entries(grouped).map(([day, group]) => (
            <div key={day}>
              <div className="overline mb-3">{day}</div>
              <div className="space-y-2">
                {group.map((n) => {
                  const Icon = ICON_BY_KIND[n.kind] || Bell;
                  return (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => openNotification(n)}
                      data-testid={`notif-item-${n.id}`}
                      className={`obs-card !p-4 flex items-center gap-4 w-full text-left transition-colors hover:border-blaze/60 ${
                        n.read ? "opacity-50" : ""
                      }`}
                    >
                      <div className="h-10 w-10 grid place-items-center bg-obsidian-900 border border-obsidian-600 rounded-sm shrink-0">
                        <Icon className="h-4 w-4 text-blaze" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-heading text-lg uppercase tracking-wide">{n.title}</div>
                        {n.body && <div className="text-xs text-zinc-400 truncate">{n.body}</div>}
                        <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-0.5">
                          {new Date(n.scheduled_for).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      {!n.read && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            markRead(n.id);
                          }}
                          className="ghost-btn px-3 py-1.5 text-[10px] inline-flex items-center gap-1.5 shrink-0"
                        >
                          <Check className="h-3 w-3" /> Done
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
